/**
 * Renders a track to a pixel-perfect canvas by baking the same collision map the
 * physics uses, then coloring each pixel by its surface. Guarantees the visuals
 * exactly match where the ball will and won't go. A glyph pass on top adds
 * directional arrows (speed tiles, one-way gates), magnet polarity markers and
 * crack lines on damaged breakable walls.
 */
import {
  Surface,
  Shape,
  buildCollisionMap,
  decodeTrackTiles,
  type Tile,
  type Track,
  type Vec2,
  FIELD_WIDTH,
  FIELD_HEIGHT,
  TILE_SIZE,
  MAP_COLS,
  MAP_ROWS,
} from '@jgolf/shared';

const COLORS: Record<number, [number, number, number]> = {
  [Surface.Grass]: [58, 124, 64],
  [Surface.Dirt]: [120, 90, 56],
  [Surface.Mud]: [74, 56, 38],
  [Surface.Ice]: [186, 224, 240],
  [Surface.Water]: [54, 122, 200],
  [Surface.Acid]: [120, 200, 70],
  [Surface.WaterSwamp]: [70, 110, 150],
  [Surface.AcidSwamp]: [110, 150, 80],
  [Surface.Block]: [90, 96, 110],
  [Surface.StickyBlock]: [120, 80, 140],
  [Surface.BouncyBlock]: [230, 110, 160],
  [Surface.FakeBlock]: [70, 130, 76],
  [Surface.OneWayN]: [110, 116, 130],
  [Surface.OneWayE]: [110, 116, 130],
  [Surface.OneWayS]: [110, 116, 130],
  [Surface.OneWayW]: [110, 116, 130],
  [Surface.Hole]: [12, 14, 18],
  [Surface.FakeHole]: [24, 30, 30],
  [Surface.Mine]: [180, 60, 50],
  [Surface.BigMine]: [200, 50, 40],
  [Surface.MagnetAttract]: [200, 170, 60],
  [Surface.MagnetRepel]: [60, 120, 200],
  [Surface.TeleBlueIn]: [70, 120, 240],
  [Surface.TeleBlueOut]: [70, 120, 240],
  [Surface.TeleRedIn]: [230, 80, 80],
  [Surface.TeleRedOut]: [230, 80, 80],
  [Surface.TeleYellowIn]: [230, 210, 70],
  [Surface.TeleYellowOut]: [230, 210, 70],
  [Surface.TeleGreenIn]: [80, 210, 110],
  [Surface.TeleGreenOut]: [80, 210, 110],
  [Surface.Breakable4]: [150, 110, 80],
  [Surface.Breakable3]: [160, 120, 86],
  [Surface.Breakable2]: [170, 130, 92],
  [Surface.Breakable1]: [180, 140, 98],
  [Surface.MoveableBlock]: [140, 120, 90],
  [Surface.MoveableBlock2]: [140, 120, 90],
  [Surface.SunkMoveable]: [96, 104, 100],
};

// Speed arrows share a tint; direction is hinted with a glyph overlay later.
for (let s = Surface.SpeedN; s <= Surface.SpeedNW; s++) COLORS[s] = [86, 150, 92];

function colorFor(s: Surface): [number, number, number] {
  return COLORS[s] ?? [58, 124, 64];
}

export function trackFromMessage(tileCodes: number[], tee: Vec2, cup: Vec2): Track {
  const tiles: Tile[] = decodeTrackTiles(tileCodes);
  return {
    id: 'net',
    name: 'net',
    author: 'net',
    tiles,
    tee,
    cup,
    par: 3,
    categories: ['basic'],
    difficulty: 'easy',
  };
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

/**
 * Paint the field to a native-resolution canvas (FIELD_WIDTH x FIELD_HEIGHT).
 * Surface colors stay pixel-accurate to the collision map; on top we apply a
 * gentle, low-frequency lighting "mottle" and a soft corner vignette for depth.
 * (The old version drew hard 2px alternating black/white stripes, which read as
 * a harsh repeating pattern once scaled up.)
 */
export function paintField(track: Track): HTMLCanvasElement {
  const map = buildCollisionMap(track);
  const canvas = document.createElement('canvas');
  canvas.width = FIELD_WIDTH;
  canvas.height = FIELD_HEIGHT;
  const cx = canvas.getContext('2d')!;
  const img = cx.createImageData(FIELD_WIDTH, FIELD_HEIGHT);
  const data = img.data;
  const W = FIELD_WIDTH;
  const H = FIELD_HEIGHT;
  for (let i = 0; i < map.cells.length; i++) {
    const [r, g, b] = colorFor(map.cells[i] as Surface);
    const x = i % W;
    const y = (i / W) | 0;
    const fx = x / W;
    const fy = y / H;
    // Smooth, large-scale brightness variation (a few low-frequency waves) so
    // the grass breathes gently instead of looking flat or striped. ~3.5%.
    const mottle =
      Math.sin(fx * 3.3 + 0.7) * Math.cos(fy * 2.1 + 0.3) * 0.5 +
      Math.sin((fx * 1.7 + fy * 2.4) * Math.PI) * 0.5;
    // Soft vignette: barely darken toward the corners for a calm, framed look.
    const dx = fx - 0.5;
    const dy = fy - 0.5;
    const vignette = 1 - smoothstep(0.3, 0.72, Math.sqrt(dx * dx + dy * dy)) * 0.2;
    const f = (1 + mottle * 0.035) * vignette;
    const o = i * 4;
    data[o] = clampByte(r * f);
    data[o + 1] = clampByte(g * f);
    data[o + 2] = clampByte(b * f);
    data[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  drawGlyphs(cx, track);
  return canvas;
}

// ─────────────────────────────────────────────────────────────
// Glyph pass: readable markers for directional / interactive tiles.
// ─────────────────────────────────────────────────────────────

const SPEED_ANGLES: Partial<Record<Surface, number>> = {
  [Surface.SpeedN]: -Math.PI / 2,
  [Surface.SpeedNE]: -Math.PI / 4,
  [Surface.SpeedE]: 0,
  [Surface.SpeedSE]: Math.PI / 4,
  [Surface.SpeedS]: Math.PI / 2,
  [Surface.SpeedSW]: (3 * Math.PI) / 4,
  [Surface.SpeedW]: Math.PI,
  [Surface.SpeedNW]: (-3 * Math.PI) / 4,
};

const ONEWAY_ANGLES: Partial<Record<Surface, number>> = {
  [Surface.OneWayN]: -Math.PI / 2,
  [Surface.OneWayE]: 0,
  [Surface.OneWayS]: Math.PI / 2,
  [Surface.OneWayW]: Math.PI,
};

function drawArrow(cx: CanvasRenderingContext2D, x: number, y: number, angle: number, size: number, color: string): void {
  cx.save();
  cx.translate(x, y);
  cx.rotate(angle);
  cx.strokeStyle = color;
  cx.lineWidth = 1.4;
  cx.lineCap = 'round';
  cx.beginPath();
  cx.moveTo(-size * 0.6, 0);
  cx.lineTo(size * 0.6, 0);
  cx.moveTo(size * 0.15, -size * 0.4);
  cx.lineTo(size * 0.62, 0);
  cx.lineTo(size * 0.15, size * 0.4);
  cx.stroke();
  cx.restore();
}

function drawGlyphs(cx: CanvasRenderingContext2D, track: Track): void {
  for (let row = 0; row < MAP_ROWS; row++) {
    for (let col = 0; col < MAP_COLS; col++) {
      const t = track.tiles[row * MAP_COLS + col];
      if (!t) continue;
      const x = col * TILE_SIZE + TILE_SIZE / 2;
      const y = row * TILE_SIZE + TILE_SIZE / 2;

      // Speed arrows (bg element, or fg with a shape)
      const speed = SPEED_ANGLES[t.bg] ?? (t.shape !== Shape.Blank ? SPEED_ANGLES[t.fg] : undefined);
      if (speed !== undefined && t.special === null) {
        drawArrow(cx, x, y, speed, 5.5, 'rgba(255,255,255,0.55)');
      }

      // One-way gates: double chevron in the allowed direction
      const oneway = ONEWAY_ANGLES[t.bg] ?? ONEWAY_ANGLES[t.fg];
      if (oneway !== undefined && t.special === null) {
        drawArrow(cx, x - Math.cos(oneway) * 2.5, y - Math.sin(oneway) * 2.5, oneway, 4, 'rgba(255,255,255,0.7)');
        drawArrow(cx, x + Math.cos(oneway) * 2.5, y + Math.sin(oneway) * 2.5, oneway, 4, 'rgba(255,255,255,0.7)');
      }

      // Magnets: polarity mark
      if (t.special === Surface.MagnetAttract || t.special === Surface.MagnetRepel) {
        const repel = t.special === Surface.MagnetRepel;
        cx.strokeStyle = repel ? 'rgba(140,200,255,0.9)' : 'rgba(255,225,130,0.9)';
        cx.lineWidth = 1.2;
        cx.beginPath();
        cx.arc(x, y, 5.5, 0, Math.PI * 2);
        cx.stroke();
        cx.beginPath();
        cx.moveTo(x - 2.5, y);
        cx.lineTo(x + 2.5, y);
        if (!repel) {
          cx.moveTo(x, y - 2.5);
          cx.lineTo(x, y + 2.5);
        }
        cx.stroke();
      }

      // Breakable walls: cracks deepen as HP drops
      if (t.special !== null && t.special >= Surface.Breakable4 && t.special <= Surface.Breakable1) {
        const stage = t.special - Surface.Breakable4; // 0 (fresh) .. 3 (crumbling)
        cx.strokeStyle = 'rgba(40,26,16,0.8)';
        cx.lineWidth = 1;
        cx.beginPath();
        for (let i = 0; i <= stage; i++) {
          const a = (i * 2.3 + col + row) % (Math.PI * 2);
          const x0 = x + Math.cos(a) * 2;
          const y0 = y + Math.sin(a) * 2;
          cx.moveTo(x0, y0);
          cx.lineTo(x0 + Math.cos(a + 0.6) * 5, y0 + Math.sin(a + 0.6) * 5);
          cx.lineTo(x0 + Math.cos(a + 0.2) * 8, y0 + Math.sin(a + 0.2) * 8);
        }
        cx.stroke();
      }

      // Mines: a warning dot ring (blown mines render flat)
      if (t.special === Surface.Mine || t.special === Surface.BigMine) {
        cx.strokeStyle = 'rgba(255,220,220,0.85)';
        cx.lineWidth = 1;
        cx.beginPath();
        cx.arc(x, y, t.special === Surface.BigMine ? 4.5 : 3.5, 0, Math.PI * 2);
        cx.stroke();
      }
    }
  }
}
